/* eslint-disable import/prefer-default-export */

import formatChartTooltipLabel from '@/services/data/format-chart-tooltip-label';
import formatCurrency from '@/services/data/format-currency';
import formatPercentage from '@/services/data/format-percentage';

// Define the colors to use for lines and bars on a chart, in order of preference.
export const chartColors = [
  '#00853f',
  '#1b8dff',
  '#ec25a1',
  '#1c3467',
  '#843893',
  '#ebd510',
  '#e01414',
  '#64c57f',
  '#00e3fc',
  '#ed9951',
  '#5ca7f2',
  '#e65cb2',
  '#96d6a8',
  '#5f74a1',
  '#8c6294',
  '#d1c44f',
  '#db6b6b',
  '#9ce7f0',
];

/**
 * A function for getting chart configuration based on chart type and output type.
 * @param {string} type - The chart type (bar or line).
 * @param {string} outputType - The output type for the chart (e.g., $billions).
 * @param {number} tab - The active tab.
 * @returns {object} An object containing chart configuration.
 */
export const getChartOptions = (type, outputType, tab) => {
  const chartOptions = {
    // Set general colors, like colors used in the legend, to black.
    color: '#000',

    plugins: {
      legend: {
        // Align the legend to the left.
        align: 'start',

        // Configure labels to be 10px by 10px circles.
        labels: {
          boxHeight: 10,
          boxWidth: 10,
          usePointStyle: true,
        },

        // Place the legend below the chart.
        position: 'bottom',
      },
      tooltip: {
        // Increase the space between the color dot and the text.
        boxPadding: 3,

        // Use color dots instead of color squares (same as the legend).
        usePointStyle: true,
      },
    },

    // Configuration for X and Y axes.
    scales: {
      x: {
        grid: {
          // Make the X baseline black and 2px thick.
          borderColor: '#000',
          borderWidth: 2,

          // Don't display any other gridlines.
          display: false,
        },
        ticks: {
          // Make the text on the X axis black.
          color: '#000',
        },
      },
      y: {
        // Ensure that the Y scale begins at zero, irrespective of the data.
        beginAtZero: true,
        grid: {
          // Make the Y baseline black and 2px thick.
          borderColor: '#000',
          borderWidth: 2,

          // Don't display any other gridlines.
          display: false,
        },
        ticks: {
          // Make the text on the Y axis black.
          color: '#000',
        },
      },
    },
  };

  // Customizations based on type.
  if (type === 'bar') {
    // Disable borders around bars on bar charts.
    chartOptions.borderWidth = 0;

    // Make the bars stacked.
    chartOptions.scales.x.stacked = true;
    chartOptions.scales.y.stacked = true;
  } else if (type === 'line') {
    // Set the width of the lines to 1px.
    chartOptions.borderWidth = 1;

    // Show hover states as long as the user is within 10px of the point on the line.
    chartOptions.pointHitRadius = 10;

    // Don't display visible points on the line - make the line smooth.
    chartOptions.pointRadius = 0;
  }

  // Customizations based on output type.
  if (outputType.includes('%') || tab >= 2) {
    chartOptions.plugins.tooltip.callbacks = {
      label: (context) => formatChartTooltipLabel(context, formatPercentage),
    };
    chartOptions.scales.y.ticks.callback = formatPercentage;
  } else if (outputType.includes('$')) {
    chartOptions.plugins.tooltip.callbacks = {
      label: (context) => formatChartTooltipLabel(context, formatCurrency),
    };
    chartOptions.scales.y.ticks.callback = formatCurrency;
  }

  // Customizations based on active tab.
  if (tab >= 2) {
    chartOptions.scales.y.stacked = true;
  }

  return chartOptions;
};
